import React, { useState } from "react";
import { useUIStore } from "../../store/useUIStore";
import { useEnhancedBeamOperations } from "./hooks/operations/useEnhancedBeamOperations";
import { useBeamTrussStore } from "../../hooks/useBeamTrussManagement";
import { EnhancedBeamContextMenu } from "../ui/EnhancedBeamContextMenu";
import { BeamPropertiesDialog } from "../ui/BeamPropertiesDialog";
import { DynamicDistributedLoadPanel } from "../loading/DynamicDistributedLoadPanel";
import { BMDInputPanel } from "../analysis/BMDInputPanel";
import { useModelStore } from "@/store/useModelStore";
import PredefinedLoadManager from "../loading/PredefinedLoadManager";
import { BeamSectionDialog } from "../ui/BeamSectionDialog";

export const EnhancedBeamInteractionComponents: React.FC = () => {
  const uiStore = useUIStore();
  const beamTrusses = useBeamTrussStore((s) => s.beamTrusses);

  const { handleEditBeamProperties, handleConfigureBMD, handleSection } =
    useEnhancedBeamOperations();

  const { selectedBeamIds } = useModelStore();

  const [addLoadDialogVisible, setAddLoadDialogVisible] = useState(false);
  const [targetBeamIds, setTargetBeamIds] = useState<string | null>(null);

  // ✅ Inline mode type: "beam" | "node" | null
  const [inlineMode, setInlineMode] = useState<"beam" | "node" | null>(null);

  const handleAddPredefinedLoadClick = (type: "beam" | "node") => {
    const targetIds = uiStore.enhancedBeamContextMenuBeamId;
    setAddLoadDialogVisible(true);
    setInlineMode(type); // ✅ now explicitly set "beam" or "node"
    setTargetBeamIds(targetIds);
    setTimeout(() => uiStore.closeEnhancedBeamContextMenu(), 100);
  };

  const predefinedLoadsSubmenu =
    beamTrusses.length > 0
      ? beamTrusses.map((truss) => ({
          id: `beam-truss-${truss.id}`,
          label: truss.label,
          icon: "📦",
          action: () => {
            const targetIds =
              selectedBeamIds && selectedBeamIds.length > 0
                ? selectedBeamIds
                : uiStore.enhancedBeamContextMenuBeamId
                  ? [uiStore.enhancedBeamContextMenuBeamId]
                  : [];

            if (targetIds.length === 0) {
              console.warn("No beam(s) selected for applying load");
              return;
            }

            // ✅ CORE API
            useBeamTrussStore.getState().applyLoadToBeams(truss.id, targetIds);
          },
        }))
      : [
          {
            id: "no-loads",
            label: "No loads defined",
            action: () => {},
          },
        ];

  const loadsSubmenu = [
    {
      id: "add-beam-load",
      label: "Add Beam Load",
      icon: "➕",
      action: () => handleAddPredefinedLoadClick("beam"),
    },
    {
      id: "predefined-loads",
      label: "Predefined Loads",
      icon: "📦",
      submenu: predefinedLoadsSubmenu,
    },
  ];

  const contextMenuItems = [
    {
      id: "loads",
      label: "Loads",
      icon: "⚙️",
      submenu: loadsSubmenu,
    },
    {
      id: "configure-bmd",
      label: "Configure BMD",
      icon: "📊",
      action: () => {
        if (uiStore.enhancedBeamContextMenuBeamId) {
          handleConfigureBMD(uiStore.enhancedBeamContextMenuBeamId);
        }
      },
    },
    {
      id: "section",
      label: "Section",
      icon: "🏗️",
      action: () => {
        if (uiStore.enhancedBeamContextMenuBeamId) {
          handleSection(uiStore.enhancedBeamContextMenuBeamId);
        }
      },
    },
    {
      id: "edit-beam-properties",
      label: "Edit Beam Properties",
      icon: "📐",
      action: () => {
        if (uiStore.enhancedBeamContextMenuBeamId) {
          handleEditBeamProperties(uiStore.enhancedBeamContextMenuBeamId);
        }
      },
    },
  ];

  return (
    <>
      <EnhancedBeamContextMenu
        x={uiStore.enhancedBeamContextMenuPosition.x}
        y={uiStore.enhancedBeamContextMenuPosition.y}
        visible={uiStore.enhancedBeamContextMenuVisible}
        items={contextMenuItems}
        onClose={() => uiStore.closeEnhancedBeamContextMenu()}
      />

      {uiStore.beamTrussConfigPanelVisible &&
        uiStore.beamTrussConfigPanelBeamId && (
          <DynamicDistributedLoadPanel
            beamId={uiStore.beamTrussConfigPanelBeamId}
            beamIds={selectedBeamIds}
            position={uiStore.beamTrussConfigPanelPosition}
            onClose={() => uiStore.closeBeamTrussConfigPanel()}
            inlineMode={false}
          />
        )}

      {uiStore.beamPropertiesDialogVisible &&
        uiStore.beamPropertiesDialogBeamId && (
          <BeamPropertiesDialog
            beamId={uiStore.beamPropertiesDialogBeamId}
            visible={uiStore.beamPropertiesDialogVisible}
            position={uiStore.beamPropertiesDialogPosition}
            onClose={() => uiStore.closeBeamPropertiesDialog()}
          />
        )}

      {uiStore.bmdInputPanelVisible && uiStore.bmdInputPanelBeamId && (
        <BMDInputPanel
          beamId={uiStore.bmdInputPanelBeamId}
          position={uiStore.bmdInputPanelPosition}
          onClose={() => uiStore.closeBMDInputPanel()}
        />
      )}

      {/* ✅ Pass inlineMode type down */}
      {addLoadDialogVisible && (
        <PredefinedLoadManager
          beamId={targetBeamIds ?? undefined}
          beamIds={selectedBeamIds}
          inlineMode={inlineMode}
          setInlineMode={setInlineMode}
          onClose={() => setAddLoadDialogVisible(false)}
        />
      )}
      {uiStore.beamSectionDialogVisible && (
        <BeamSectionDialog
          beamIds={uiStore.beamSectionDialogBeamIds}
          visible={uiStore.beamSectionDialogVisible}
          position={uiStore.beamSectionDialogPosition}
          onClose={() => uiStore.closeBeamSectionDialog()}
        />
      )}
    </>
  );
};

export default EnhancedBeamInteractionComponents;
